import env from "../variables/config";

const apiUrl = env.apiUrl;

function authHeaders(json = false) {
    const h: Record<string, string> = {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
    };
    if (json) h["Content-Type"] = "application/json";
    return h;
}

export interface GhostSignal {
    type: string;
    date: string | null;
    source: string;
}

export interface GhostEventEnrichment {
    type: string;
    status: string;
    hasData: boolean;
}

export interface GhostEvent {
    id: string;
    companyName: string;
    enrichments: GhostEventEnrichment[];
    approvedCount: number;
    usableCount: number;
}

export interface GhostRecord {
    id: string;
    name: string;
    legalName: string;
    siret: string;
    siren: string;
    status: string;
    isActive: boolean;
    hide: boolean;
    nafCode: string;
    address: { street: string; postcode: string; city: string };
    phone: string;
    email: string;
    website: string;
    openingDate: string | null;
    closingDate: string | null;
    createdAt: string | null;
    peopleCount: number;
    signals: GhostSignal[];
    event: GhostEvent | null;
}

export interface GhostMatch {
    nameSimilarity: number;
    sameCity: boolean;
    samePostcode: boolean;
    sharedPeople: number;
}

export interface GhostCandidate extends GhostRecord {
    match: GhostMatch;
}

export interface GhostCluster {
    siren: string;
    bestScore: number;
    ghost: GhostRecord;
    candidates: GhostCandidate[];
}

export interface GhostQueueResponse {
    items: GhostCluster[];
    /** True when a cap was hit before the end of the base — more remains to scan. */
    truncated: boolean;
    maxItems: number;
    /** SIRENs inspected by this call alone, not since the beginning. */
    sirensInspected: number;
    /** Cursor to pass back as `after` to continue; null once the scan reached the end. */
    nextAfter: string | null;
}

async function handle(res: Response) {
    if (!res.ok) {
        let message = `HTTP ${res.status}`;
        try {
            message = (await res.json())?.error || message;
        } catch (_) {}
        throw new Error(message);
    }
    return res.json();
}

export async function fetchGhostQueue(after?: string | null): Promise<GhostQueueResponse> {
    const query = after ? `?after=${encodeURIComponent(after)}` : "";
    const res = await fetch(`${apiUrl}/admin/ghosts${query}`, { headers: authHeaders() });
    return handle(res);
}

export async function mergeGhost(ghostId: string, targetId: string): Promise<{ carries: string[] }> {
    const res = await fetch(`${apiUrl}/admin/ghosts/${ghostId}/merge`, {
        method: "POST",
        headers: authHeaders(true),
        body: JSON.stringify({ targetId }),
    });
    return handle(res);
}

export async function deleteGhost(ghostId: string): Promise<void> {
    const res = await fetch(`${apiUrl}/admin/ghosts/${ghostId}`, {
        method: "DELETE",
        headers: authHeaders(),
    });
    await handle(res);
}

export async function keepGhost(ghostId: string): Promise<void> {
    const res = await fetch(`${apiUrl}/admin/ghosts/${ghostId}/keep`, {
        method: "POST",
        headers: authHeaders(),
    });
    await handle(res);
}
